import React, { Component } from 'react';
import './Sustainability.css';

class Sustainability extends Component {
  constructor(props) {
    super(props);
    this.state = {
      carbonNeutral: 0,
      reducedCarbon: 0,
      garmentsUpcycled: 0,
      treesPlanted: 0,
      visibleSections: {}
    };
    this.observer = null;
    this.sectionRefs = [];
  }

  componentDidMount() {
    this.animateNumbers();
    this.setupScrollAnimations();
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  animateNumbers() {
    this.animateCounter('carbonNeutral', 100, 2000);
    this.animateCounter('reducedCarbon', 40, 2000);
    this.animateCounter('garmentsUpcycled', 10000, 2000);
    this.animateCounter('treesPlanted', 15000, 2000);
  }

  animateCounter(key, target, duration) {
    let start = null;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const value = Math.floor(progress * target);
      this.setState({ [key]: value });
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }

  setupScrollAnimations() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.setState(prevState => ({
              visibleSections: {
                ...prevState.visibleSections,
                [entry.target.id]: true
              }
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    ['pillars', 'sdg', 'timeline'].forEach(id => {
      const element = document.getElementById(id);
      if (element) this.observer.observe(element);
    });
  }

  render() {
    const { carbonNeutral, reducedCarbon, garmentsUpcycled, treesPlanted, visibleSections } = this.state;

    const pillars = [
      { icon: '🌱', title: 'Ethical Sourcing', desc: 'Partnering with certified suppliers who prioritize fair wages and safe working conditions.' },
      { icon: '🔄', title: 'Circular Design', desc: 'Creating timeless pieces with durable materials that last for years.' },
      { icon: '📦', title: 'Eco Packaging', desc: 'Using 100% recycled and compostable packaging materials.' },
      { icon: '🚚', title: 'Green Logistics', desc: 'Carbon-neutral shipping with electric vehicle delivery options.' },
      { icon: '💧', title: 'Water Conservation', desc: 'Reducing water usage by 75% through innovative dyeing techniques.' },
      { icon: '🌳', title: 'Reforestation', desc: 'Planting a tree for every product sold through our partnership.' }
    ];

    const sdgs = [
      { number: 12, title: 'Responsible Consumption', desc: 'Implementing circular economy principles', progress: 90 },
      { number: 13, title: 'Climate Action', desc: 'Reducing carbon emissions across supply chain', progress: 85 },
      { number: 8, title: 'Decent Work', desc: 'Ensuring fair wages and safe working conditions', progress: 95 },
      { number: 6, title: 'Clean Water', desc: 'Minimizing water pollution in production', progress: 80 }
    ];

    const timeline = [
      { year: '2018', title: 'Founded', desc: 'Started with 100% sustainable materials' },
      { year: '2020', title: 'Carbon Neutral', desc: 'Achieved carbon-neutral operations' },
      { year: '2022', title: 'B Corp Certified', desc: 'Received B Corporation certification' },
      { year: '2023', title: 'Circular Program', desc: 'Launched garment recycling initiative' },
      { year: '2024', title: 'Next Goals', desc: 'Targeting 100% renewable energy' }
    ];

    return (
      <div className="sustainability">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-bg">
            <div className="gradient"></div>
          </div>
          <div className="container">
            <div className="hero-content">
              <div className="badge">🌱 Sustainable Since 2018</div>
              <h1>Fashion with Purpose</h1>
              <p className="subtitle">Creating beautiful, sustainable clothing that cares for people and our planet.</p>
              <div className="hero-stats">
                <div className="stat">
                  <div className="number">{carbonNeutral}%</div>
                  <div className="label">Carbon Neutral</div>
                </div>
                <div className="stat">
                  <div className="number">{reducedCarbon}%</div>
                  <div className="label">Reduced Emissions</div>
                </div>
                <div className="stat">
                  <div className="number">{garmentsUpcycled.toLocaleString()}+</div>
                  <div className="label">Garments Upcycled</div>
                </div>
                <div className="stat">
                  <div className="number">{treesPlanted.toLocaleString()}+</div>
                  <div className="label">Trees Planted</div>
                </div>
              </div>
              <button className="cta-btn" onClick={() => document.getElementById('commitment').scrollIntoView({ behavior: 'smooth' })}>
                Explore Our Journey ↓
              </button>
            </div>
          </div>
        </section>

        {/* Commitment Section */}
        <section id="commitment" className="section commitment">
          <div className="container">
            <div className="section-header">
              <span className="tag">Our Mission</span>
              <h2>Building a Sustainable Future</h2>
              <p>We're committed to transforming fashion through innovation, transparency, and responsibility.</p>
            </div>
            <div className="commitment-grid">
              <div className="card">
                <div className="card-icon">🌍</div>
                <h3>Planet Positive</h3>
                <p>Going beyond sustainability to actively restore and regenerate natural systems.</p>
              </div>
              <div className="card">
                <div className="card-icon">👥</div>
                <h3>People First</h3>
                <p>Ensuring fair wages, safe conditions, and community development across our supply chain.</p>
              </div>
              <div className="card">
                <div className="card-icon">🔍</div>
                <h3>Radical Transparency</h3>
                <p>Sharing our entire production process, challenges, and progress openly.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pillars Section */}
        <section id="pillars" className={`section pillars ${visibleSections.pillars ? 'visible' : ''}`}>
          <div className="container">
            <div className="section-header">
              <span className="tag">Our Approach</span>
              <h2>Our Sustainability Pillars</h2>
              <p>Six fundamental principles that guide every decision we make.</p>
            </div>
            <div className="pillars-grid">
              {pillars.map((pillar, index) => (
                <div key={index} className="pillar-card">
                  <div className="pillar-icon">{pillar.icon}</div>
                  <h3>{pillar.title}</h3>
                  <p>{pillar.desc}</p>
                  <div className="pillar-stat">
                    <span className="check">✓</span>
                    <span>Active</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="section impact">
          <div className="container">
            <div className="impact-content">
              <div className="impact-text">
                <div className="impact-badge">🔥 Climate Positive</div>
                <h2>Our Environmental Impact</h2>
                <p>We remove twice as much carbon as we emit through verified offset projects and regenerative agriculture.</p>
                <div className="impact-stats">
                  <div className="impact-stat">
                    <div className="big-number">2x</div>
                    <div>Carbon removed vs emitted</div>
                  </div>
                  <div className="impact-stat">
                    <div className="big-number">100%</div>
                    <div>Renewable energy in operations</div>
                  </div>
                </div>
              </div>
              <div className="impact-visual">
                <div className="chart">
                  <div className="chart-title">Carbon Impact</div>
                  <div className="chart-bars">
                    <div className="bar-group">
                      <div className="bar-label">Emissions</div>
                      <div className="bar emissions" style={{ width: '40%' }}></div>
                    </div>
                    <div className="bar-group">
                      <div className="bar-label">Removals</div>
                      <div className="bar removals" style={{ width: '80%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section id="timeline" className="section timeline">
          <div className="container">
            <div className="section-header">
              <span className="tag">Our Journey</span>
              <h2>Milestones in Sustainability</h2>
            </div>
            <div className="timeline-container">
              {timeline.map((item, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-year">{item.year}</div>
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SDG Section */}
        <section id="sdg" className={`section sdg ${visibleSections.sdg ? 'visible' : ''}`}>
          <div className="container">
            <div className="section-header">
              <span className="tag">Global Goals</span>
              <h2>UN Sustainable Development Goals</h2>
              <p>We're proud to contribute to these global targets for a better world.</p>
            </div>
            <div className="sdg-grid">
              {sdgs.map((goal, index) => (
                <div key={index} className="sdg-card">
                  <div className="sdg-header">
                    <div className="sdg-number">{goal.number}</div>
                    <div className="sdg-icon">⚡</div>
                  </div>
                  <h3>{goal.title}</h3>
                  <p>{goal.desc}</p>
                  <div className="progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${goal.progress}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">{goal.progress}% Alignment</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section cta">
          <div className="container">
            <div className="cta-content">
              <h2>Join the Movement</h2>
              <p>Every purchase supports our mission to make fashion sustainable and ethical.</p>
              <div className="cta-buttons">
                <button className="btn primary" onClick={() => window.location.href = '/shop'}>Shop Sustainable Collection</button>
                <button className="btn secondary" onClick={() => window.location.href = '/impact'}>View Impact Report</button>
              </div>
              <div className="certifications">
                <div className="cert">
                  <span>🌱</span>
                  <span>B Corp Certified</span>
                </div>
                <div className="cert">
                  <span>♻️</span>
                  <span>Climate Neutral</span>
                </div>
                <div className="cert">
                  <span>✓</span>
                  <span>Fair Trade</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
}

export default Sustainability;